import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser } from '../auth';

export default function DevenirOrganisateur() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = getUser();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adresse, setAdresse] = useState('');
  const [date, setDate] = useState('');
  const [heure, setHeure] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [adresseValide, setAdresseValide] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchActivity();
  }, [id]);

  const fetchActivity = async () => {
    const { data } = await supabase.from('activites').select('*').eq('id', id).single();
    if (data) setActivity(data);
    setLoading(false);
  };

  const handleAdresse = async (val) => {
    setAdresse(val);
    setAdresseValide(false);
    setCoords(null);
    if (val.length < 3) { setSuggestions([]); return; }
    try {
      const ville = data?.lieu || 'Valence';
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(val + ' ' + ville)}&limit=5`);
      const data2 = await res.json();
      setSuggestions(data2.features || []);
    } catch (e) { setSuggestions([]); }
  };

  const selectAdresse = (s) => {
    setAdresse(s.properties.label);
    setCoords({ lat: s.geometry.coordinates[1], lon: s.geometry.coordinates[0] });
    setAdresseValide(true);
    setSuggestions([]);
  };

  const publier = async () => {
    if (!adresse.trim()) { alert('Entre une adresse'); return; }
    if (!date) { alert('Choisis une date'); return; }
    if (!heure) { alert('Choisis une heure'); return; }
    setSaving(true);

    // Créer une nouvelle activité basée sur le template
    const { data: newActivite, error } = await supabase.from('activites').insert({
      titre: activity.titre,
      type: activity.type,
      emoji: activity.emoji,
      lieu: adresse,
      date: date,
      heure: heure,
      age_min: activity.age_min,
      age_max: activity.age_max,
      max_familles: activity.max_familles,
      max_enfants: activity.max_enfants,
      description: activity.description,
      regles: activity.regles,
      latitude: coords?.lat || null,
      longitude: coords?.lon || null,
      photo_url: activity.photo_url,
      user_id: user.id,
      is_template: false,
      template_type: activity.template_type,
    }).select().single();

    if (error) { alert('Erreur. Réessaie !'); setSaving(false); return; }

    // Inscrire automatiquement l'organisateur comme participant
    await supabase.from('participations').insert({
      activite_id: newActivite.id,
      user_id: user.id,
      nb_enfants: 1,
    });

    // Notifier Wooki
    await supabase.from('notifications').insert({
      user_id: '844324a7-263b-45cd-9e15-8978820e1972',
      type: 'nouvelle_activite',
      message: `${user.prenom} organise "${activity.titre}" a ${adresse} le ${new Date(date).toLocaleDateString('fr-FR')}`,
      activite_id: newActivite.id,
      lu: false,
    });

    setSaving(false);
    navigate(`/detail/${newActivite.id}`);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF7' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Chargement...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7' }}>

      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg, #FF8F00, #FFB300)', padding: '48px 24px 28px' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 100, padding: '8px 16px', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', marginBottom: 20 }}>← Retour</button>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🙋</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.6rem', color: 'white', marginBottom: 6 }}>
          Je deviens organisateur !
        </div>
        <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
          {activity?.emoji} {activity?.titre} · {activity?.lieu}
        </div>
      </div>

      <div style={{ padding: '28px 24px 80px' }}>

        {/* INFO */}
        <div style={{ background: '#EDF7F1', borderRadius: 16, padding: '14px 16px', marginBottom: 24, display: 'flex', gap: 10 }}>
          <span style={{ fontSize: '1.2rem' }}>💡</span>
          <div style={{ fontSize: '0.82rem', color: '#4A6355', lineHeight: 1.6 }}>
            Wooki s'occupe des règles, du podium et de la communication. Toi tu choisis juste <strong>où, quand et à quelle heure</strong> !
          </div>
        </div>

        {/* ADRESSE */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            📍 Adresse exacte du lieu de rendez-vous
          </label>
          <div style={{ fontSize: '0.75rem', color: '#8A9E8D', marginBottom: 10 }}>
            Parc, terrain de sport, espace vert... dans {activity?.lieu}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              value={adresse}
              onChange={e => handleAdresse(e.target.value)}
              placeholder={`Ex: Parc Jouvet, ${activity?.lieu}`}
              style={{ width: '100%', padding: '14px 16px', border: `2px solid ${adresseValide ? '#3D9A6A' : '#E8EFE9'}`, borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: '#FAFAF7', outline: 'none', boxSizing: 'border-box' }}
            />
            {adresseValide && (
              <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#3D9A6A', fontSize: '1.2rem' }}>✅</div>
            )}
            {suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(28,43,30,0.12)', zIndex: 100, overflow: 'hidden', marginTop: 4 }}>
                {suggestions.map((s, i) => (
                  <div key={i} onClick={() => selectAdresse(s)} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '0.85rem', color: '#1C2B1E', borderBottom: i < suggestions.length - 1 ? '1px solid #F3F4F2' : 'none', display: 'flex', gap: 10 }}>
                    <span>📍</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>{s.properties.label}</div>
                      {s.properties.context && <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{s.properties.context}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DATE + HEURE */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>📅 Date</label>
              <input
                type="date"
                value={date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setDate(e.target.value)}
                style={{ width: '100%', padding: '14px 16px', border: '2px solid #E8EFE9', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: '#FAFAF7', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>⏱ Heure</label>
              <input
                type="time"
                value={heure}
                onChange={e => setHeure(e.target.value)}
                style={{ width: '100%', padding: '14px 16px', border: '2px solid #E8EFE9', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: '#FAFAF7', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </div>

        {/* RÉCAP */}
        {activity && (
          <div style={{ background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', borderRadius: 24, padding: '16px 18px', marginBottom: 24 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#3D9A6A', marginBottom: 12 }}>✅ Wooki s'occupe du reste</div>
            {[
              `📋 Règles officielles "${activity.titre}"`,
              `👨‍👩‍👧 Jusqu'à ${activity.max_familles} familles`,
              `🧒 ${activity.max_enfants} enfants maximum`,
              `👶 Pour les ${activity.age_min}–${activity.age_max} ans`,
              `🏆 Podium Wooki après l'activité`,
            ].map((r, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#4A6355', padding: '4px 0', borderBottom: i < 4 ? '1px solid #C5DFC9' : 'none' }}>{r}</div>
            ))}
          </div>
        )}

        {/* ENGAGEMENT */}
        <div style={{ background: 'white', borderRadius: 20, padding: '14px 16px', marginBottom: 20, border: '2px solid #E8EFE9' }}>
          <div style={{ fontSize: '0.82rem', color: '#4A6355', lineHeight: 1.6 }}>
            🤝 En devenant organisateur je m'engage à :
          </div>
          {[
            'Être présent le jour J avec mes enfants',
            'Respecter et faire respecter les règles Wooki',
            'Accueillir chaleureusement toutes les familles',
            'Prévenir les participants en cas d\'annulation',
          ].map((e, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: '#8A9E8D', padding: '4px 0', paddingLeft: 8, borderBottom: i < 3 ? '1px solid #F3F4F2' : 'none' }}>✓ {e}</div>
          ))}
        </div>

        <button onClick={publier} disabled={saving || !adresse || !date || !heure} style={{ width: '100%', padding: '17px', background: saving || !adresse || !date || !heure ? '#E8EFE9' : 'linear-gradient(135deg, #FF8F00, #FFB300)', color: saving || !adresse || !date || !heure ? '#8A9E8D' : 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: saving || !adresse || !date || !heure ? 'not-allowed' : 'pointer', boxShadow: saving || !adresse || !date || !heure ? 'none' : '0 6px 20px rgba(255,143,0,0.4)' }}>
          {saving ? '⏳ Publication...' : '🙋 Je publie cette activité !'}
        </button>

      </div>
    </div>
  );
}